/**
 *
 *   audio card:   idle --HEARD--> heard --RECORD_START--> recording
 *                    ^                                        |
 *                    |                                   RECORD_DONE
 *                    |                                        v
 *                 (wrong) <---- RESULT(false) ----------- checking
 *                                                             |
 *                                                        RESULT(true)
 *                                                             v
 *                                                          correct
 *
 *   word-only card (hasAudio:false, no speaker): starts at `ready` — the mic is
 *   live immediately, nothing to hear first.
 *
 * Rules enforced here:
 *   - Hear before Say: an audio card's mic is disabled until its speaker is
 *     pressed (status reaches `heard`).
 *   - One at a time: only the `activeKey` card can record; others are blocked
 *     while it is recording/checking (kills the cross-card grading race).
 *   - Unlimited retries: a wrong answer returns the card to a retryable state;
 *     there is no attempt cap.
 */

export const EXIT_MIN_SECONDS = 180; // B: may leave early after 3 minutes
export const EXIT_MAX_SECONDS = 300; // C: pushed out after 5 minutes

export function initState(items = []) {
  const cards = {};
  const order = [];
  for (const it of items) {
    cards[it.key] = {
      key: it.key,
      text: it.text,
      hasAudio: !!it.hasAudio,
      status: it.hasAudio ? "idle" : "ready", // audio: hear first; word-only: ready
      tries: 0,
    };
    order.push(it.key);
  }
  return { cards, order, activeKey: null };
}

function patch(state, key, changes) {
  return {
    ...state,
    cards: { ...state.cards, [key]: { ...state.cards[key], ...changes } },
  };
}

export function reviewReducer(state, action) {
  const { type, key } = action;
  const card = key != null ? state.cards[key] : null;

  switch (type) {
    case "HEARD": {
      // Pressing the speaker enables the mic. Allowed any time the card is not
      // mid-record and not already passed.
      if (!card) return state;
      if (["idle", "heard", "wrong"].includes(card.status)) {
        return patch(state, key, { status: "heard" });
      }
      return state;
    }
    case "RECORD_START": {
      if (!canRecord(state, key)) return state;
      return { ...patch(state, key, { status: "recording" }), activeKey: key };
    }
    case "RECORD_DONE": {
      if (!card || state.activeKey !== key) return state;
      return patch(state, key, { status: "checking" });
    }
    case "RESULT": {
      if (!card || state.activeKey !== key) return state;
      const correct = !!action.correct;
      return {
        ...patch(state, key, {
          status: correct ? "correct" : "wrong",
          tries: card.tries + 1,
        }),
        activeKey: null,
      };
    }
    case "RECORD_CANCEL": {
      // recording aborted (silence / error) — return to a retryable state
      if (!card || state.activeKey !== key) return state;
      return {
        ...patch(state, key, { status: card.hasAudio ? "heard" : "ready" }),
        activeKey: null,
      };
    }
    default:
      return state;
  }
}


/** Can this card start recording right now? (serialized on activeKey) */
export function canRecord(state, key) {
  const card = state.cards[key];
  if (!card) return false;
  if (state.activeKey && state.activeKey !== key) return false;
  return ["ready", "heard", "wrong"].includes(card.status);
}

/** Should the mic look live for this card? */
export function micEnabled(state, key) {
  const card = state.cards[key];
  if (!card) return false;
  if (!card.hasAudio) return true; // word-only cards: mic always live
  return ["heard", "wrong", "recording", "checking"].includes(card.status);
}

export const isCorrect = (state, key) => state.cards[key]?.status === "correct";
export const isWrong = (state, key) => state.cards[key]?.status === "wrong";

/** A (finish): every card has been said correctly. */
export function allDone(state) {
  return (
    state.order.length > 0 &&
    state.order.every((k) => state.cards[k].status === "correct")
  );
}

/** Go! enabled: finished, or the 3-minute early-exit window is open. */
export function canExit(state, elapsedSeconds) {
  return allDone(state) || elapsedSeconds >= EXIT_MIN_SECONDS;
}

/** Forced exit at 5 minutes. */
export function mustExit(elapsedSeconds) {
  return elapsedSeconds >= EXIT_MAX_SECONDS;
}

export function progress(state) {
  const total = state.order.length;
  const done = state.order.filter((k) => state.cards[k].status === "correct").length;
  return { done, total };
}
